/**
 * Prediction Market layer renderer.
 * Renders Polymarket events with geo-coordinates as bubble markers.
 * Bubble size reflects 24h trading volume, color reflects probability.
 */

import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const PREDICTION_MARKET_LAYER_IDS = new Set(['prediction-markets']);

// ---------------------------------------------------------------------------
// Color by probability
// ---------------------------------------------------------------------------

type RGBA = [number, number, number, number];

/** Color gradient from low probability (cool blue) to high probability (warm red). */
function getProbabilityColor(topPrice: number): RGBA {
  if (topPrice >= 0.8) return [239, 68, 68, 200];    // red — very likely
  if (topPrice >= 0.6) return [245, 158, 11, 190];   // amber — likely
  if (topPrice >= 0.4) return [234, 179, 8, 180];    // yellow — toss-up
  if (topPrice >= 0.2) return [59, 130, 246, 170];   // blue — unlikely
  return [100, 116, 139, 160];                       // slate — very unlikely
}

// ---------------------------------------------------------------------------
// Sizing by volume
// ---------------------------------------------------------------------------

function getVolumeRadius(volume: number): number {
  if (volume >= 500_000) return 14;
  if (volume >= 100_000) return 11;
  if (volume >= 50_000) return 9;
  if (volume >= 10_000) return 7;
  if (volume >= 1_000) return 5;
  return 4;
}

/** Extract topPrice from event raw data. */
function getTopPrice(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (raw && typeof raw.topPrice === 'number') return raw.topPrice;
  return 0.5;
}

/** Extract 24h volume from event raw data. */
function getVolume(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (raw && typeof raw.volume24hr === 'number') return raw.volume24hr;
  return 0;
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

/** Create deck.gl layers for prediction market geo-overlay. */
export function createPredictionMarketLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  // Only include events with geo-coordinates
  const geoEvents = events.filter(e => e.location);
  if (geoEvents.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.4 + zoom * 0.08);

  // Outer glow — volume-based radius, semi-transparent
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'prediction-markets-glow',
    data: geoEvents,
    pickable: false,
    filled: true,
    radiusUnits: 'pixels' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => {
      const [r, g, b] = getProbabilityColor(getTopPrice(d));
      return [r, g, b, 30] as RGBA;
    },
    getRadius: (d) => getVolumeRadius(getVolume(d)) * 2,
    opacity: opacity * 0.5,
    updateTriggers: {
      getFillColor: [geoEvents.length],
      getRadius: [geoEvents.length],
    },
  }));

  // Core bubble
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'prediction-markets-core',
    data: geoEvents,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 1,
    radiusUnits: 'pixels' as const,
    radiusMinPixels: 3,
    radiusMaxPixels: 16,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => getProbabilityColor(getTopPrice(d)),
    getLineColor: [255, 255, 255, 100],
    getLineWidth: 1,
    getRadius: (d) => getVolumeRadius(getVolume(d)),
    opacity,
    updateTriggers: {
      getFillColor: [geoEvents.length],
      getRadius: [geoEvents.length],
    },
  }));

  // Probability labels at higher zoom
  if (zoom >= 4) {
    layers.push(new TextLayer<NormalizedEvent>({
      id: 'prediction-markets-labels',
      data: geoEvents,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getText: (d) => {
        const pct = Math.round(getTopPrice(d) * 100);
        const title = d.title.length > 30 ? d.title.slice(0, 27) + '...' : d.title;
        return `${pct}% ${title}`;
      },
      getSize: 10,
      getColor: [255, 255, 255, 200],
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [16, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 180],
    }));
  }

  return layers;
}
